
/* eslint-disable */
import React, { useState, useEffect, memo,useRef } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  CalendarOutlined,
  CloudDownloadOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  DownOutlined,
  DownloadOutlined,

} from "@ant-design/icons";
import { AiOutlineFilter } from "react-icons/ai";
import { Progress, Tooltip } from "antd";

import Table from "./Table";
import Loader from "./Loader";
// NOTE: If you truly don't use Date filters or search, you can remove these later.
import {
  getImportListing,
  getTemplatesList,
} from "../redux/authSlice";

const clamp = (n: number, min = 0, max = 100) => Math.max(min, Math.min(max, n));
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

// re-animate ko avoid karne ke liye per-row memory (optional)
const playedOnce = new Map<string | number, boolean>();

type ImportListingProps = {
  fromDate?: string;
  thruDate?: string;
  facilityId?: string;
  statusId?: string;
  queryString?: string;
  importType?: string; 
  showLoader?: boolean;
  shouldRerender?: boolean;
  setShouldRerender?: (b: boolean) => void;
  shipmentId?: string;
  setErrorFileNotification?: (v: any) => void;
  errorFileNotification?: any;
  orderId?: any;
};

// ---- ProgressCell (animated 0 -> percent) ----
type ProgressCellProps = {
  percent: number;               
  ok?: number;                    
  total?: number;                 
  duration?: number;              
  playOnceKey?: string | number; 
};

const ProgressCell: React.FC<ProgressCellProps> = ({
  percent,
  ok = 0,
  total = 0,
  duration = 900,
  playOnceKey,

}) => {
  const [p, setP] = React.useState(0);
  const rafRef = React.useRef<number | null>(null);
  const target = clamp(Math.round(percent || 0));

  React.useEffect(() => {
    // same row pe pehle animate ho chuka ho to direct target dikhaye
    if (playOnceKey && playedOnce.get(playOnceKey)) {
      setP(target);
      return;
    }
    const from = 0, to = target, start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const next = Math.round(from + (to - from) * easeOutCubic(t));
      setP(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else if (playOnceKey) playedOnce.set(playOnceKey, true);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration, playOnceKey]);

  return (
    <div style={{ minWidth: 180 }}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span>{p}%</span>
        {total ? <span>{ok}/{total}</span> : null}
      </div>
      <Progress percent={p} size="small" showInfo={false}/>
    </div>
  );
};

// Component and Function for Records Count
type CountUpProps = {
  target: number;                 
  duration?: number;              
  playOnceKey?: string | number;  
  formatter?: (n: number) => React.ReactNode; 
};

const CountUp: React.FC<CountUpProps> = ({
  target,
  duration = 800,
  playOnceKey,
  formatter,
}) => {
  const [val, setVal] = useState(0);
  const rafRef = useRef<number | null>(null);
  const safeTarget = Math.max(0, Math.floor(target || 0));

  useEffect(() => {
    // same row/field pe pehle animate ho chuka to seedha target dikhao
    if (playOnceKey && playedOnce.get(playOnceKey)) {
      setVal(safeTarget);
      return;
    }

    const from = 0;
    const to = safeTarget;
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const next = Math.round(from + (to - from) * easeOutCubic(t));
      setVal(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else if (playOnceKey) {
        playedOnce.set(playOnceKey, true);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [safeTarget, duration, playOnceKey]);

  return <span>{formatter ? formatter(val) : val.toLocaleString()}</span>;
};

const ImportListing = memo(
  ({
    fromDate,
    thruDate,
    facilityId,
    statusId,
    queryString,
    importType,
    showLoader = true,
    shouldRerender,
    setShouldRerender,
    shipmentId,
    setErrorFileNotification,
    errorFileNotification,
    orderId
  }: ImportListingProps) => {
    const dispatch = useDispatch();
    const location = useLocation();

    // pagination / data
    const [currIndex, setCurrIndex] = useState<any>("1");
    const [isLoading, setIsLoading] = useState(false);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [getImportListingDetail, setgetImportListingDetail] = useState<any>("");
    const [recordCount, setRecordCount] = useState("");
    const [maxPageIndex, setMaxPageIndex] = useState<any>("");
    const [sortData, setSortData] = useState<any>("");

    // filters & state
    const [templateList, setTemplateList] = useState<any[]>([]);
    const [activeAppliedFilters, setActiveAppliedFilters] = useState("");
    const [filteredInfo, setFilteredInfo] = useState<any>({});
    const [sortedInfo, setSortedInfo] = useState<any>({});

    const [selectedFromDate, setSelectedFromDate] = useState(
      fromDate
        ? fromDate
        : localStorage.getItem("importListingFromDate")
          ? (localStorage.getItem("importListingFromDate") as string)
          : ""
    );
    const [selectedThruDate, setSelectedThruDate] = useState(
      thruDate
        ? thruDate
        : localStorage.getItem("importListingThruDate")
          ? (localStorage.getItem("importListingThruDate") as string)
          : ""
    );

    const [pageSize, setPageSize] = useState<any>();

    // Expanded row error preview cache
    const [rowErrors, setRowErrors] = useState<
      Record<string, { text: string; loading: boolean; error?: string }>
    >({});

    useEffect(() => {
      fetchData();
      return () => { };
      // eslint-disable-next-line
    }, [location.key]);

    useEffect(() => {
      if (shouldRerender) {
        fetchData();
        setShouldRerender && setShouldRerender(false);
      }
      // eslint-disable-next-line
    }, [shouldRerender]);

    const fetchData = async () => {
      if (fromDate) localStorage.setItem("importListingFromDate", fromDate);
      if (thruDate) localStorage.setItem("importListingThruDate", thruDate);

      let prevAppliedFilter: any = localStorage.getItem("prevAppliedFilter");
      if (!prevAppliedFilter) {
        prevAppliedFilter = {
          appliedFilters: importType ? `templateIdList=${importType}` : "",
        };
        setActiveAppliedFilters(prevAppliedFilter?.appliedFilters);
      } else {
        prevAppliedFilter = JSON.parse(prevAppliedFilter);
        prevAppliedFilter = {
          ...prevAppliedFilter,
          appliedFilters: importType ? `templateIdList=${importType}` : "",
        };
        setSortedInfo(prevAppliedFilter?.sortedInfo);
        setFilteredInfo(prevAppliedFilter?.filteredInfo);
        setActiveAppliedFilters(prevAppliedFilter?.appliedFilters);
      }

      setIsLoading(true);

      await dispatch(
        getImportListing({
          setgetImportListingDetail,
          setMaxPageIndex,
          orderByFields: prevAppliedFilter?.orderByFields
            ? prevAppliedFilter?.orderByFields
            : sortData,
          setRecordCount,
          pageIndex: localStorage.getItem("currPageIndex")
            ? localStorage.getItem("currPageIndex")
            : 0,
          pageSize,
          uploadedOn_from: selectedFromDate
            ? selectedFromDate
            : fromDate
              ? fromDate
              : "",
          uploadedOn_thru: selectedThruDate
            ? selectedThruDate
            : thruDate
              ? thruDate
              : "",
          statusId,
          facilityId: facilityId ? facilityId : "",
          queryString,
          appliedFilters: prevAppliedFilter?.appliedFilters
            ? prevAppliedFilter.appliedFilters
            : "",
          shipmentId,
          setErrorFileNotification,
          errorFileNotification,
          orderId
        })
      );
      await dispatch(getTemplatesList({ setTemplateList }));
      setIsLoading(false);
    };

    const nextSetOfProds = async (
      pageIndex: any,
      orderByFields: any,
      appliedFilters: any,
      _resetFields?: any,
      pageSizeParam?: any
    ) => {
      setIsTableLoading(true);
      localStorage.setItem("currPageIndex", pageIndex);

      await dispatch(
        getImportListing({
          setgetImportListingDetail,
          setMaxPageIndex,
          orderByFields,
          setRecordCount,
          pageIndex,
          pageSize: pageSizeParam,
          uploadedOn_from: selectedFromDate
            ? selectedFromDate
            : fromDate
              ? fromDate
              : "",
          uploadedOn_thru: selectedThruDate
            ? selectedThruDate
            : thruDate
              ? thruDate
              : "",
          statusId,
          queryString,
          facilityId: facilityId ? facilityId : "",
          appliedFilters,
          shipmentId,
          orderId
        })
      );

      setIsTableLoading(false);
    };

    const loadNextProds = (index: any, pageSizeVal: any) => {
      if (!currIndex || currIndex * 1 === 0) return;
      if (currIndex * 1 > maxPageIndex) return;

      const passedIndex = index * 1 - 1;
      setCurrIndex(index);

      let prevAppliedFilter: any = localStorage.getItem("prevAppliedFilter");
      prevAppliedFilter = JSON.parse(prevAppliedFilter);

      let orderByFields = sortData;
      if (prevAppliedFilter?.orderByFields) {
        orderByFields = prevAppliedFilter?.orderByFields;
      }

      if (pageSizeVal > 10) setPageSize(pageSizeVal);

      nextSetOfProds(
        passedIndex,
        orderByFields,
        activeAppliedFilters,
        false,
        pageSizeVal
      );
    };

    const download = (data: any, which:
      "uploadedFileLocation" | "successFileLocation" | "errorFileLocation") => {
      window.location.href =
        process.env.REACT_APP_API_BASE_URL +
        `/fp-static/downloadDataImportFile?dataImportId=${data?.dataImportId}&fileToDownload=${which}`;
    };

    const fetchErrorPreview = async (dataImportId: string) => {
      if (rowErrors[dataImportId]?.text || rowErrors[dataImportId]?.loading) return;
      setRowErrors((prev) => ({ ...prev, [dataImportId]: { text: "", loading: true } }));
      try {
        const url =
          process.env.REACT_APP_API_BASE_URL +
          `/fp-static/downloadDataImportFile?dataImportId=${dataImportId}&fileToDownload=errorFileLocation`;
        const resp = await fetch(url, { credentials: "include" });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const text = await blob.text();
        const limited = text.split("\n").slice(0, 200).join("\n");
        setRowErrors((prev) => ({
          ...prev,
          [dataImportId]: { text: limited, loading: false },
        }));
      } catch (e: any) {
        setRowErrors((prev) => ({
          ...prev,
          [dataImportId]: { text: "", loading: false, error: e?.message || "Failed to load" },
        }));
      }
    };

    const columns = [
      {
        title: <div className="flex items-center relative h-5 p-2 leading-3"><p className="mr-1">Data Import ID</p></div>,
        dataIndex: "dataImportId",
        filteredValue: filteredInfo?.dataImportId || null,
        onFilter: (value: any, record: any) => record?.dataImportId?.includes(value),
        filterIcon: () =>
          filteredInfo.dataImportId ? (
            <AiOutlineFilter size={17} color="#093479" />
          ) : (
            <AiOutlineFilter size={17} />
          ),
        sorter: {
          compare: (a: any, b: any) =>
            typeof a.dataImportId === "number"
              ? a.dataImportId - b.dataImportId
              : (a.dataImportId || "").localeCompare(b.dataImportId || ""),
          multiple: 1,
        },
        sortOrder:
          sortedInfo?.dataImportId === "dataImportId" &&
          sortedInfo?.dataImportIdType,
        render: (dataImportId: string) => <h1 className="px-2">{dataImportId}</h1>,
      },
      {
        title: <div className="flex items-center relative h-5 p-2 leading-3"><p className="mr-1">Date Uploaded</p></div>,
        dataIndex: "uploadedOn",
        filterIcon: () =>
          filteredInfo.uploadedOn ? (
            <AiOutlineFilter size={17} color="#093479" />
          ) : (
            <AiOutlineFilter size={17} />
          ),
        sorter: {
          compare: (a: any, b: any) =>
            typeof a.uploadedOn === "number"
              ? a.uploadedOn - b.uploadedOn
              : (a.uploadedOn || "").localeCompare(b.uploadedOn || ""),
          multiple: 1,
        },
        sortOrder:
          sortedInfo?.uploadedOn === "uploadedOn" && sortedInfo?.uploadedOnType,
        render: (uploadedOn: any) => {
          const txt = uploadedOn ? new Date(uploadedOn).toDateString() : "";
          return (
            <div className="flex flex-row items-center">
              {uploadedOn ? <CalendarOutlined style={{ fontSize: 18, color: "#999" }} /> : null}
              <h1 className="ml-1.5">{txt}</h1>
            </div>
          );
        },
      },
      {
        title: <div className="flex items-center relative h-5 p-2 leading-3"><p className="mr-1">Filename</p></div>,
        dataIndex: "fileName",
        filterIcon: () =>
          filteredInfo?.fileName ? (
            <AiOutlineFilter size={17} color="#093479" />
          ) : (
            <AiOutlineFilter size={17} />
          ),
        sorter: {
          compare: (a: any, b: any) =>
            typeof a.fileName === "number"
              ? a.fileName - b.fileName
              : (a.fileName || "").localeCompare(b.fileName || ""),
          multiple: 1,
        },
        filteredValue: filteredInfo?.fileName || null,
        onFilter: (value: any, record: any) => record?.fileName?.includes(value),
        sortOrder: sortedInfo?.fileName === "fileName" && sortedInfo?.fileNameType,
        render: (fileName: string) => <div className="flex"><h1 className="ml-2">{fileName}</h1></div>,
      },
      {
        title: <div className="flex items-center relative h-5 p-2 leading-3"><p className="mr-1">Status</p></div>,
        dataIndex: "statusId",
        filterIcon: () =>
          filteredInfo.statusId ? (
            <AiOutlineFilter size={17} color="#093479" />
          ) : (
            <AiOutlineFilter size={17} />
          ),
        sorter: {
          compare: (a: any, b: any) =>
            typeof a.statusId === "number"
              ? a.statusId - b.statusId
              : (a.statusId || "").localeCompare(b.statusId || ""),
          multiple: 1,
        },
        filteredValue: filteredInfo?.statusId || null,
        onFilter: (value: any, record: any) => record?.statusId?.includes(value),
        sortOrder: sortedInfo?.statusId === "statusId" && sortedInfo?.statusIdType,
        render: (statusId: any) => {
          const s = (statusId || "").toUpperCase();
          const color =
            s === "SUCCESSFUL" ? "#16a34a" :
              s === "INPROGRESS" ? "#093479" :
                s === "FAILURE" ? "#dc2626" : "#475569";
          return (
            <span
              className="px-1.5 py-0.5 text-white tracking-wider font-semibold rounded-md"
              style={{ backgroundColor: color, border: "1px solid #707070" }}
            >
              {s}
            </span>
          );
        },
      },
      {
        title: <div className="flex items-center relative h-5 p-2 leading-3"><p className="mr-1">Template</p></div>,
        dataIndex: "templateId",
        filterIcon: () =>
          filteredInfo?.templateId ? (
            <AiOutlineFilter size={17} color="#093479" />
          ) : (
            <AiOutlineFilter size={17} />
          ),
        sorter: {
          compare: (a: any, b: any) =>
            typeof a.templateId === "number"
              ? a.templateId - b.templateId
              : (a.templateId || "").localeCompare(b.templateId || ""),
          multiple: 1,
        },
        filteredValue: filteredInfo?.templateId || null,
        onFilter: (value: any, record: any) => record?.templateId?.includes(value),
        sortOrder:
          sortedInfo?.templateId === "templateId" && sortedInfo?.templateIdType,
        render: (templateId: string) => {
          const templateName: any = templateList?.find(
            (t: any) => t?.templateId === templateId
          );
          return <div className="flex"><h1 className="ml-2">{templateName?.templateName}</h1></div>;
        },
      },
      // RECORDS (Total / Success / Errors)
      {
        title: <div className="flex items-center relative h-5 p-2 leading-3"><p className="mr-1">Records</p></div>,
        key: "records",
        render: (_: any, rec: any) => {
          const total = Number(rec?.uploadedRecords || 0);
          const ok = Number(rec?.successRecords || 0);
          const err = Number(rec?.errorRecords ?? Math.max(total - ok, 0));
          return (
            <div className="leading-5">
              <div><b>Total:</b> {total || ""}</div>
              <div>Success: {ok || ""}</div>
              <div>Errors: {err || ""}</div>
            </div>
          );
        },
  //         render: (_: any, rec: any) => {
  //   const total = Number(rec?.uploadedRecords || 0);
  //   const ok    = Number(rec?.successRecords  || 0);
  //   const err   = Number(rec?.errorRecords ?? Math.max(total - ok, 0));
  //   const id    = rec?.dataImportId ?? rec?.id ?? Math.random();

  //   return (
  //     <div className="leading-5 tabular-nums">
  //       <div>
  //         <b>Total:</b>{" "}
  //         {total > 0 ? (
  //           <CountUp target={total} duration={900} playOnceKey={`${id}-total`} />
  //         ) : (
  //           ""
  //         )}
  //       </div>

  //       <div>
  //         Success:{" "}
  //         {ok > 0 ? (
  //           <CountUp target={ok} duration={900} playOnceKey={`${id}-ok`} />
  //         ) : (
  //           ""
  //         )}
  //       </div>

  //       <div>
  //         Errors:{" "}
  //         {err > 0 ? (
  //           <CountUp target={err} duration={900} playOnceKey={`${id}-err`} />
  //         ) : (
  //           ""
  //         )}
  //       </div>
  //     </div>
  //   );
  // },
      },
      // PROGRESS
      {
        title: <div className="flex items-center relative h-5 p-2 leading-3"><p className="mr-1">Progress</p></div>,
        key: "progress",
        width: 130,
        // render: (_: any, rec: any) => {
        //   const total = Number(rec?.uploadedRecords || 0);
        //   const ok = Number(rec?.successRecords || 0);
        //   const pct =
        //     total > 0
        //       ? Math.round((ok / total) * 100)
        //       : (rec?.statusId || "").toUpperCase() === "SUCCESSFUL"
        //       ? 100
        //       : 0;
        //   return (
        //     <div style={{ minWidth: 180 }}>
        //       <div className="flex items-center justify-between text-xs mb-1">
        //         <span>{pct}%</span>
        //         {total ? <span>{ok}/{total}</span> : null}
        //       </div>
        //       <Progress percent={pct} size="small" />
        //     </div>
        //   );
        // },
        render: (_: any, rec: any) => {
          const total = Number(rec?.uploadedRecords || 0);
          const ok = Number(rec?.successRecords || 0);
          const pct =
            total > 0
              ? Math.round((ok / total) * 100)
              : (rec?.statusId || "").toUpperCase() === "SUCCESSFUL"
                ? 100
                : 0;

          return (
            <ProgressCell
              percent={pct}
              ok={ok}
              total={total}
              duration={2000}
              playOnceKey={rec?.dataImportId ?? rec?.id}
            />
          );
        },
      },
      // ACTIONS
      {
        title: <div className="flex items-center relative h-5 p-2 leading-3"><p className="mr-1">Actions</p></div>,
        key: "actions",
        render: (_: any, data: any) => {
          return (
            <div className="flex items-center gap-3">
              {data?.uploadedFileLocation && (
                <Tooltip title="Download uploaded file">
                  <DownloadOutlined
                    className="text-blue-600"
                    onClick={() => download(data, "uploadedFileLocation")}
                    style={{ fontSize: 18, cursor: "pointer" }}
                  />
                </Tooltip>
              )}
              {data?.successFileLocation && (
                <Tooltip title="Download success file">
                  <DownloadOutlined
                    className="text-green-600"
                    onClick={() => download(data, "successFileLocation")}
                    style={{ fontSize: 18, cursor: "pointer" }}
                  />
                </Tooltip>
              )}
              {data?.errorFileLocation && (
                <Tooltip title="Download error file">
                  <DownloadOutlined
                    className="text-red-600"
                    onClick={() => download(data, "errorFileLocation")}
                    style={{ fontSize: 18, cursor: "pointer" }}
                  />
                </Tooltip>
              )}
            </div>
          );
        },
      },
    ].filter((column: any) => {
      if (!importType || column?.dataIndex !== "templateId") {
        return column;
      }
      return null;
    });

    function onChange(pagination: any, filters: any, sorter: any) {
      let appliedFilters = importType ? `templateIdList=${importType}` : "";

      if (filters.dataImportId) {
        appliedFilters += appliedFilters
          ? `&dataImportId=${filters.dataImportId}`
          : `dataImportId=${filters.dataImportId}`;
      }
      if (filters.fileName) {
        appliedFilters += appliedFilters
          ? `&fileName=${filters.fileName}`
          : `fileName=${filters.fileName}`;
      }
      if (filters.templateId) {
        appliedFilters += appliedFilters
          ? `&templateIdList=${importType ?? filters.templateId}`
          : `templateIdList=${importType ?? filters.templateId}`;
      }
      if (filters.statusId && filters?.statusId.length > 0) {
        const joinedStr = filters?.statusId.join(" OR ");
        if (!appliedFilters) {
          appliedFilters += `queryString=statusId: ${joinedStr}`;
        } else {
          const filtersArr = appliedFilters.split("&");
          const idx = filtersArr.findIndex((it: any) => it.includes("queryString"));
          if (idx > -1) {
            let extractedQuery = filtersArr[idx];
            extractedQuery += ` AND statusId: ${joinedStr}`;
            filtersArr[idx] = extractedQuery;
            appliedFilters = filtersArr.join("&");
          } else {
            appliedFilters += `&queryString=statusId: ${joinedStr}`;
          }
        }
      }

      setFilteredInfo(filters);
      setActiveAppliedFilters(appliedFilters);

      let outputStr = "";
      setSortData("");

      if (Array.isArray(sorter)) {
        // multiple sorters
        let modifiedObj: any = {};
        sorter?.forEach((item: any) => {
          modifiedObj = {
            ...modifiedObj,
            [item?.field]: item?.field,
            [`${item?.field}Type`]: item?.order,
          };
          if (item.order === "ascend") {
            outputStr += outputStr ? `,${item.field}` : item.field;
          } else if (item.order === "descend") {
            outputStr += outputStr ? `,-${item.field}` : `-${item.field}`;
          }
        });
        setSortedInfo(modifiedObj);
      } else if (sorter && Object.keys(sorter)?.length > 0) {
        setSortedInfo({
          [sorter?.field]: sorter?.field,
          [`${sorter?.field}Type`]: sorter?.order,
        });
        if (sorter.order === "ascend") {
          outputStr = `${sorter.field}`;
        } else if (sorter.order === "descend") {
          outputStr = `-${sorter.field}`;
        }
      }

      setSortData((prev: any) => (prev ? prev + `,${outputStr}` : outputStr));

      const prevOrderListingValue = {
        orderByFields: outputStr,
        appliedFilters,
        sortedInfo: {
          [sorter?.field]: sorter?.field,
          [`${sorter?.field}Type`]: sorter?.order,
        },
        filteredInfo: filters,
      };
      localStorage.setItem("prevAppliedFilter", JSON.stringify(prevOrderListingValue));

      if (appliedFilters) {
        nextSetOfProds(0, outputStr, appliedFilters, true, pageSize);
      } else if (recordCount) {
        nextSetOfProds(0, outputStr, "", true, pageSize);
      }
    }

    return (
      <div className={!importType ? "h-screen" : ""}>
        <Loader
          isLoading={showLoader && isLoading}
          styles={{
            overlay: (base: any) => ({
              ...base,
              backgroundColor: "white",
            }),
          }}
        >
          <div className="bg-white p-5 pb-0.5 rounded-lg shadow-sm">
            <div className="flex flex-row justify-between">
              <div className="m-3">
                <div className="flex flex-row items-center">
                  <button
                    onClick={async () => {
                      setIsTableLoading(true);
                      await dispatch(
                        getImportListing({
                          setgetImportListingDetail,
                          setMaxPageIndex,
                          orderByFields: "",
                          setRecordCount,
                          pageIndex: currIndex * 1 - 1,
                          pageSize,
                          uploadedOn_from: selectedFromDate
                            ? selectedFromDate
                            : fromDate
                              ? fromDate
                              : "",
                          uploadedOn_thru: selectedThruDate
                            ? selectedThruDate
                            : thruDate
                              ? thruDate
                              : "",
                          statusId,
                          queryString,
                          facilityId: facilityId ? facilityId : "",
                          appliedFilters: activeAppliedFilters,
                          shipmentId,
                          orderId
                        })
                      );
                      setSortedInfo({});
                      setSortData("");
                      setIsTableLoading(false);
                    }}
                    className="ml-0 mb-2 px-8 text-xs sm:px-3 sm:text-sm bg-transparent focus:outline-none border border-black text-black font-semibold py-2 rounded-lg"
                  >
                    Clear Sorter
                  </button>
                </div>
              </div>
            </div>

            <Table
              rowKey="dataImportId"
              dataSource={getImportListingDetail}
              columns={columns}
              isServerRendered={!!recordCount}
              isPaginated={true}
              loadNextProds={loadNextProds}
              recordCount={recordCount}
              onChange={onChange}
              loading={isTableLoading}
              expandable={{
                expandRowByClick: false,
                rowExpandable: (rec: any) => {
                  const s = (rec?.statusId || "").toUpperCase();
                  const hasErr =
                    Number(rec?.errorRecords || 0) > 0 || s === "FAILURE";
                  return !!rec?.errorFileLocation || hasErr;
                },
                expandIcon: ({ expanded, onExpand, record }: { expanded: any, onExpand: any, record: any }) => (
                  <button
                    aria-label={expanded ? "Collapse row" : "Expand row"}
                    onClick={(e) => onExpand(record, e)}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 0,
                      lineHeight: 1,
                    }}
                  >
                    {expanded ? (
                      <DownOutlined style={{ fontSize: 14 }} />
                    ) : (
                      <RightOutlined style={{ fontSize: 14 }} />
                    )}
                  </button>
                ),
                expandedRowRender: (rec: any) => {
                  const id = String(rec?.dataImportId);
                  if (!rowErrors[id]) fetchErrorPreview(id);
                  const state = rowErrors[id];
                  return (
                    <div className="p-3 bg-gray-50 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <ExclamationCircleOutlined style={{ color: "#dc2626" }} />
                        <b>Error details</b>
                        <span className="text-xs text-gray-500">
                          (Data Import ID: {id})
                        </span>
                      </div>
                      {state?.loading && <div>Loading error preview…</div>}
                      {state?.error && (
                        <div className="text-red-600">
                          Failed to load: {rec.errorMessage}
                        </div>
                      )}
                      {!state?.loading && !state?.error && (
                        <pre className="whitespace-pre-wrap text-xs max-h-64 overflow-auto border rounded p-2 bg-white">
                          {rec.errorMessage || "No error details available."}
                        </pre>
                      )}
                      {rec?.errorFileLocation && (
                        <button
                          className="mt-2 px-3 py-1 rounded bg-[#093479] text-white"
                          onClick={() =>
                            download(rec, "errorFileLocation")
                          }
                        >
                          Download full error file
                        </button>
                      )}
                    </div>
                  );
                },
              }}
            />
          </div>
        </Loader>
      </div>
    );
  }
);

export default ImportListing;
